<template>
  <div
    v-if="availableAddresses.length > 1"
    v-click-away="close"
    class="address-filter"
  >
    <div
      class="address-filter__placeholder address-filter-placeholder"
      :class="{ active: showDropdown }"
      @click="toggleDropdown"
    >
      <div
        v-if="selectedAddresses.length > 0"
        class="address-filter-placeholder__selected placeholder-selected"
      >
        <div class="placeholder-selected__first selected-first">
          <div class="selected-first__name">
            {{ displayAddress(selectedAddresses[0]) }}
          </div>
          <div
            v-if="selectedAddresses.length > 1"
            class="selected-first__number"
          >
            +{{ selectedAddresses.length - 1 }}
          </div>
        </div>
        <div
          v-if="selectedAddresses.length > 0"
          class="placeholder-selected__empty"
          @click.prevent="emptySelectedList"
        />
      </div>
      <div v-else>
        {{ placeholder }}
      </div>
    </div>

    <div
      v-if="showDropdown"
      class="address-filter__dropdown address-filter-dropdown"
      @click="$event.stopPropagation()"
    >
      <div class="input-text input-text--filter">
        <input
          v-model="searchText"
          type="text"
          :placeholder="trans('filter.address.search')"
        >
      </div>
      <ul>
        <li
          v-for="address in addressesFiltered"
          :key="address.id"
          class="input-checkbox"
        >
          <input
            :id="'address_' + address.id"
            v-model="selectedAddresses"
            type="checkbox"
            :value="address"
          >
          <label
            :for="'address_' + address.id"
          ><span>{{ displayAddress(address) }}</span></label>
        </li>
        <li v-if="addressesFiltered.length === 0" class="empty">
          {{ trans('filter.address.empty') }}
        </li>
      </ul>
    </div>
    <div class="address-filter__save" @click="saveFavoriteAddresses">
      <span v-if="!isSaving">{{ trans('filter.address.save') }}</span>
      <Spinner v-else />
    </div>
    <div v-if="wasRecentlySaved" class="address-filter__save-confirm">
      <span>{{ trans('filter.address.response_confirm') }}</span>
    </div>
  </div>
</template>

<script>
import { addressToString } from '@elements/address-utils';
import { storeToRefs } from 'pinia';
import { useScreensizeStore } from '@stores/useScreensizeStore.ts';
import { useAddressFilterStore } from '@stores/useAddressFilterStore';
import { useFavoriteAddressesStore } from '@stores/useFavoriteAddressesStore';
import Spinner from '@components/Spinner.vue';
import { translationMixin } from '@elements/translation';
import { requestsMixin } from '@tools/requests-mixin';
import { eventBus } from '@tools/event-bus';

export default {
  components: { Spinner },
  mixins: [ translationMixin, requestsMixin ],
  setup() {
    useAddressFilterStore().init();

    const { isTabletLarge, isDesktop } = storeToRefs(useScreensizeStore());
    const { addressesIds: favoriteAddressesIds, wasRecentlySaved, isSaving } = storeToRefs(useFavoriteAddressesStore());
    const { availableAddresses, selectedAddresses } = storeToRefs(useAddressFilterStore());

    return {
      isTabletLarge,
      isDesktop,
      availableAddresses,
      selectedAddresses,
      favoriteAddressesIds,
      wasRecentlySaved,
      isSaving,
    };
  },
  data() {
    return {
      showDropdown: false,
      searchText: '',
    };
  },
  computed: {
    fixedSearchText() {
      return this.searchText.toLowerCase().trim();
    },
    placeholder() {
      return this.trans('filter.address.placeholder');
    },
    addressesFiltered() {
      return this.availableAddresses
        .filter((address) => this.addressSearchText(address).includes(this.fixedSearchText))
        .sort((a, b) => (this.displayAddress(a) >= this.displayAddress(b) ? 1 : -1));
    },
  },
  mounted() {
    /**
     * This listener purpose is to handle click outside of the button
     */
    window.addEventListener('click', () => {
      if (this.showDropdown) {
        this.showDropdown = false;
      }
    });
    eventBus.addEventListener('offer-close-modale', () => {
      if (this.showDropdown) {
        this.showDropdown = false;
      }
    });
    eventBus.addEventListener('filters-modal-click-outside', this.handleFiltersModalClickOutside);
  },
  beforeUnmount() {
    eventBus.removeEventListener('filters-modal-click-outside', this.handleFiltersModalClickOutside);
  },
  methods: {
    handleFiltersModalClickOutside() {
      if (this.showDropdown) {
        this.showDropdown = false;
      }
    },
    async saveFavoriteAddresses() {
      await useFavoriteAddressesStore().saveAddresses(this.selectedAddresses);
    },
    toggleDropdown(event) {
      if (event) {
        event.stopPropagation();
      }

      if (this.showDropdown) {
        this.showDropdown = false;
        return;
      }
      eventBus.dispatchEvent(new CustomEvent('offer-close-modale'));
      this.showDropdown = true;

      if (this.isTabletLarge || this.isDesktop) {
        this.$nextTick(() => {
          document.querySelector('.address-filter input[type=text]').focus();
        });
      }
    },
    emptySelectedList() {
      this.selectedAddresses = [];
    },
    displayAddress(address) {
      if (address.name) {
        return address.name;
      }

      return addressToString(address);
    },
    addressSearchText(address) {
      return `${addressToString(address).toLowerCase().trim()} ${(address.name ?? '').toLowerCase().trim()}`;
    },
    close() {
      this.$emit('close');
    },
  },
};
</script>
